import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { 
  Search, 
  Sparkles, 
  FileText, 
  Loader2, 
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  X,
  Wand2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AISearch = ({ onSelectDocument }) => {
  const { token } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [summarizing, setSummarizing] = useState(false);
  const [summary, setSummary] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    setShowResults(true);
    setResults([]);
    
    try {
      const response = await axios.post(`${API_URL}/api/ai/search`, {
        query: searchQuery,
        search_type: 'semantic'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setResults(response.data.results || []);
      
      if (response.data.results?.length === 0) {
        toast.info('No matching documents found');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handleSummarize = async (doc) => {
    setSelectedDoc(doc);
    setSummarizing(true);
    setSummary(null);
    
    try {
      const response = await axios.post(`${API_URL}/api/ai/summarize/${doc.id}`, {
        document_id: doc.id,
        summary_type: 'brief'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSummary(response.data.summary);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate summary');
    } finally {
      setSummarizing(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      draft: { color: 'bg-gray-500', icon: Clock, label: 'Draft' },
      pending_approval: { color: 'bg-yellow-500', icon: AlertCircle, label: 'Pending' },
      approved: { color: 'bg-green-500', icon: CheckCircle, label: 'Approved' },
      rejected: { color: 'bg-red-500', icon: XCircle, label: 'Rejected' }
    };
    const { color, icon: Icon, label } = config[status] || config.draft;
    return (
      <Badge className={`${color} text-white text-xs`}>
        <Icon className="w-3 h-3 mr-1" />
        {label}
      </Badge>
    );
  };

  return (
    <>
      {/* Search Input */}
      <form onSubmit={handleSearch} className="relative">
        <div className="relative flex items-center gap-2">
          <div className="relative flex-1">
            <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-500" />
            <Input
              type="text"
              placeholder="AI Search: Find documents by meaning..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 bg-background border-purple-200 focus:border-purple-500"
              data-testid="ai-search-input"
            />
          </div>
          <Button 
            type="submit" 
            disabled={searching || !searchQuery.trim()}
            className="bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600"
            data-testid="ai-search-btn"
          >
            {searching ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Search className="w-4 h-4 mr-2" />
                Search
              </>
            )}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-1 ml-1">
          <Sparkles className="w-3 h-3 inline mr-1 text-purple-400" />
          Powered by AI - Search by meaning, not just keywords
        </p>
      </form>

      {/* Search Results Dialog */}
      <Dialog open={showResults} onOpenChange={setShowResults}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              AI Search Results
            </DialogTitle>
          </DialogHeader>
          
          <div className="text-sm text-muted-foreground mb-4">
            Query: &quot;<span className="text-foreground font-medium">{searchQuery}</span>&quot;
            {results.length > 0 && (
              <span className="ml-2">• {results.length} document{results.length !== 1 ? 's' : ''} found</span>
            )}
          </div>
          
          <div className="flex-1 overflow-y-auto space-y-3">
            {searching ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="relative">
                  <Loader2 className="w-10 h-10 animate-spin text-purple-500" />
                  <Sparkles className="w-4 h-4 text-purple-400 absolute -top-1 -right-1 animate-pulse" />
                </div>
                <p className="text-muted-foreground mt-4">Searching with AI...</p>
              </div>
            ) : results.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <Search className="w-10 h-10 mb-4 opacity-50" />
                <p>No documents matched your query</p>
                <p className="text-xs mt-1">Try rephrasing or using different terms</p>
              </div>
            ) : (
              results.map((doc) => (
                <Card key={doc.id} className="hover:shadow-md transition-shadow" data-testid={`search-result-${doc.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <h4 className="font-medium truncate">{doc.name}</h4>
                        </div>
                        {doc.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                            {doc.description}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-2">
                          {getStatusBadge(doc.status)}
                          {doc.document_type && (
                            <Badge variant="outline" className="text-xs">
                              {doc.document_type.code}
                            </Badge>
                          )}
                          {doc.document_number && (
                            <span className="text-xs text-muted-foreground font-mono">
                              #{doc.document_number}
                            </span>
                          )}
                        </div>
                        {doc.tags?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {doc.tags.slice(0, 3).map((tag, i) => (
                              <Badge key={i} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleSummarize(doc)}
                          disabled={summarizing && selectedDoc?.id === doc.id}
                          data-testid={`summarize-btn-${doc.id}`}
                        >
                          {summarizing && selectedDoc?.id === doc.id ? (
                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                          ) : (
                            <Wand2 className="w-3 h-3 mr-1" />
                          )}
                          Summarize
                        </Button>
                        {onSelectDocument && (
                          <Button
                            size="sm"
                            onClick={() => {
                              onSelectDocument(doc);
                              setShowResults(false);
                            }}
                          >
                            Open
                          </Button>
                        )}
                      </div>
                    </div>
                    
                    {/* Summary section */}
                    {selectedDoc?.id === doc.id && summary && (
                      <div className="mt-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-purple-500" />
                          <span className="text-sm font-medium text-purple-700 dark:text-purple-300">AI Summary</span>
                        </div>
                        <p className="text-sm text-foreground">{summary}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AISearch;
